import React, { useRef, useMemo, useCallback, memo } from "react";
import i18n from "i18next";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { Switch, Route, Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavigationPrompt from "react-router-navigation-prompt";
import { NavigationDialog, Wizard } from "../../../../components";
import {
  buildNewRecipientList,
  validateRecipientsFile,
  validateFilterCriteria,
  saveNewRecipientList,
  uploadFilterMasterRecipients,
} from "../../../../actions/newRecipientList";
import { notify } from "../../../../actions/notification";
import Upload from "../File/Upload";
import FilterCriteria from "./FilterCriteria";
import Save from "../Save";

const useStyles = makeStyles((theme) => ({
  wizard: {},
}));

const getSteps = ({
  t,
  validateFile,
  validateCriteria,
  criteriaScrollerRef,
}) => [
  {
    label: t("Upload File"),
    path: "/recipient-lists/create/upload-filter/upload",
    component: Upload,
    validate: validateFile,
  },
  {
    label: t("Filter Criteria"),
    path: "/recipient-lists/create/upload-filter/filter-criteria",
    component: () => <FilterCriteria scrollerRef={criteriaScrollerRef} />,
    validate: validateCriteria,
  },
  {
    label: t("Save"),
    path: "/recipient-lists/create/upload-filter/save",
    component: Save,
  },
];

function CreateRecipientListFromUploadFilter({ onAbort, afterSave, children }) {
  const criteriaScrollerRef = useRef();
  const dispatch = useDispatch();
  const newRecipientList = useSelector((state) => state.newRecipientList);
  const { doneStepIdx } = newRecipientList;
  const classes = useStyles();
  const { t } = useTranslation();

  const validateFile = useCallback(() => dispatch(validateRecipientsFile()), [
    dispatch,
  ]);

  const validateCriteria = useCallback(() => {
    const isValid = dispatch(validateFilterCriteria());
    if (!isValid && criteriaScrollerRef.current) {
      criteriaScrollerRef.current.scrollTop = 0;
    }
    return isValid;
  }, [dispatch, criteriaScrollerRef]);

  const steps = useMemo(
    () => getSteps({ t, validateFile, validateCriteria, criteriaScrollerRef }),
    [t, validateFile, validateCriteria]
  );

  const onStepChange = useCallback(
    (activeStepIdx) =>
      dispatch(buildNewRecipientList({ activeStepIdx, errors: {} })),
    [dispatch]
  );

  const onSave = useCallback(async () => {
    // Re-validate right before the actual submit, not just when the user first left the Filter
    // Criteria step - the mapping can go stale (e.g. the Master List's config changed since it was
    // loaded). If a mapped field no longer supports filtering, stop here: no list should be
    // created for a mapping that's guaranteed to fail server-side.
    const isValid = dispatch(validateFilterCriteria());
    if (!isValid) {
      dispatch(
        notify({
          type: "error",
          message: i18n.t(
            "Fields added for filtering do not support filtration. Please review the Filter Criteria step."
          ),
        })
      );
      return;
    }

    const recipientListId = await dispatch(saveNewRecipientList());
    await dispatch(uploadFilterMasterRecipients());
    if (afterSave) {
      afterSave(recipientListId);
    }
  }, [dispatch, afterSave]);

  return (
    <>
      <NavigationPrompt
        when={(crntLocation, nextLocation, _action) =>
          !nextLocation || !nextLocation.pathname.startsWith("/recipient-lists")
        }
      >
        {({ onConfirm, onCancel, isActive }) => (
          <NavigationDialog
            onConfirm={onConfirm}
            onCancel={onCancel}
            open={isActive}
          />
        )}
      </NavigationPrompt>
      <Wizard
        steps={steps}
        className={classes.wizard}
        doneStepIdx={doneStepIdx}
        onStepChange={onStepChange}
        disableNext={
          Object.keys(newRecipientList.errors).length ||
          newRecipientList.loading
        }
        errors={newRecipientList.errors}
        onSave={onSave}
        onAbort={onAbort}
      />
      {children}
      <Switch>
        {steps.slice(0, doneStepIdx + 2).map((step) => (
          <Route key={step.path} path={step.path} component={step.component} />
        ))}
        <Redirect to={steps[newRecipientList.doneStepIdx + 1].path} />
      </Switch>
    </>
  );
}

export default memo(CreateRecipientListFromUploadFilter);
